<template>
  <div class="video-manager">
    <div class="manager-header">
      <h2>Course Videos</h2>
      <button @click="showCreateModal = true" class="btn btn-primary">
        <i class="fas fa-plus"></i>
        Add Video
      </button>
    </div>

    <!-- Course Filter -->
    <div class="filter-section">
      <label>Filter by Course:</label>
      <select v-model="selectedCourseFilter" @change="loadVideos" class="form-select">
        <option :value="null">All Courses</option>
        <option v-for="course in courses" :key="course.id" :value="course.id">
          {{ course.title }}
        </option>
      </select>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading videos...</p>
    </div>

    <!-- Videos List -->
    <div v-else class="videos-list">
      <div
        v-for="video in videos"
        :key="video.id"
        class="video-item"
      >
        <div class="video-info">
          <div class="video-header">
            <h3>{{ video.title }}</h3>
            <div class="video-badges">
              <span class="type-badge">{{ video.video_type }}</span>
              <span v-if="video.video_url" class="url-badge">
                <i class="fab fa-vimeo"></i>
                External URL
              </span>
            </div>
          </div>
          <p v-if="video.description" class="video-description">
            {{ video.description }}
          </p>
          <div class="video-meta">
            <span>
              <i class="fas fa-book"></i>
              {{ video.course_title || 'No course' }}
            </span>
            <span v-if="video.duration_seconds">
              <i class="fas fa-clock"></i>
              {{ formatDuration(video.duration_seconds) }}
            </span>
            <span>
              <i class="fas fa-sort-numeric-down"></i>
              Order: {{ video.order }}
            </span>
            <span v-if="pollingVideoIds.includes(video.id)" class="moments-badge moments-badge--indexing">
              <i class="fas fa-spinner fa-spin"></i>
              Indexing moments…
            </span>
            <span v-else-if="video.moments_count > 0" class="moments-badge">
              <i class="fas fa-map-marker-alt"></i>
              {{ video.moments_count }} moment{{ video.moments_count === 1 ? '' : 's' }}
            </span>
          </div>
          <div v-if="video.video_url" class="video-url">
            <i class="fas fa-link"></i>
            <a :href="video.video_url" target="_blank" rel="noopener">
              {{ video.video_url }}
            </a>
          </div>
        </div>
          <div class="video-actions">
          <button @click="editVideo(video)" class="btn btn-small btn-secondary">
            <i class="fas fa-edit"></i>
            Edit
          </button>
          <button 
            v-if="video.video_url && video.video_url.includes('vimeo')" 
            @click="analyzeVideo(video)" 
            class="btn btn-small btn-success"
          >
            <i class="fas fa-robot"></i>
            AI Analyze
          </button>
          <button
            @click="generateMoments(video)"
            :disabled="generatingMomentsFor === video.id || pollingVideoIds.includes(video.id)"
            class="btn btn-small btn-moments"
            :title="pollingVideoIds.includes(video.id) ? 'Indexing in progress…' : (video.moments_count > 0 ? `Re-generate (${video.moments_count} existing moments)` : 'Index video moments for semantic search')"
          >
            <i v-if="generatingMomentsFor === video.id || pollingVideoIds.includes(video.id)" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-map-marker-alt"></i>
            {{ generatingMomentsFor === video.id ? 'Queuing…' : pollingVideoIds.includes(video.id) ? 'Indexing…' : (video.moments_count > 0 ? 'Re-index' : 'Gen Moments') }}
          </button>
          <button @click="manageTimestamps(video)" class="btn btn-small btn-primary">
            <i class="fas fa-clock"></i>
            Timestamps
          </button>
          <button @click="manageMaterials(video)" class="btn btn-small btn-info">
            <i class="fas fa-folder-open"></i>
            Materials
          </button>
          <button @click="deleteVideo(video)" class="btn btn-small btn-danger">
            <i class="fas fa-trash"></i>
            Delete
          </button>
        </div>
      </div>

      <div v-if="videos.length === 0" class="empty-state">
        <i class="fas fa-video"></i>
        <h3>No videos yet</h3>
        <p>Add your first video to a course</p>
        <button @click="showCreateModal = true" class="btn btn-primary">
          <i class="fas fa-plus"></i>
          Add Video
        </button>
      </div>
    </div>

    <!-- Video Creation Wizard (for new videos) -->
    <VideoCreationWizard
      v-if="showCreateModal && !editingVideo"
      :visible="showCreateModal"
      :courses="courses"
      @close="closeModal"
      @video-created="handleVideoCreated"
    />

    <!-- Edit Modal (for editing existing videos) -->
    <div v-if="editingVideo" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Edit Video</h2>
          <button @click="closeModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Basic Info -->
          <div class="form-section">
            <h3>Basic Information</h3>
            
            <div class="form-group">
              <label>Course *</label>
              <select v-model="formData.course" class="form-select" required>
                <option :value="null">Select a course</option>
                <option v-for="course in courses" :key="course.id" :value="course.id">
                  {{ course.title }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Title *</label>
              <input
                v-model="formData.title"
                type="text"
                placeholder="Video title"
                class="form-input"
                required
              />
            </div>

            <div class="form-group">
              <label>Description</label>
              <textarea
                v-model="formData.description"
                placeholder="What will students learn in this video?"
                class="form-textarea"
                rows="3"
              ></textarea>
            </div>
          </div>

          <!-- Video Source -->
          <div class="form-section">
            <h3>
              <i class="fab fa-vimeo"></i>
              Video Source
            </h3>
            
            <div class="video-source-info">
              <p>
                <strong>Vimeo Integration:</strong> Paste your Vimeo video URL below. 
                Supports both regular Vimeo links and embed URLs.
              </p>
              <p class="example-text">
                Example: <code>https://vimeo.com/123456789</code> or 
                <code>https://player.vimeo.com/video/123456789</code>
              </p>
            </div>

            <div class="form-group">
              <label>
                <i class="fab fa-vimeo"></i>
                Video URL (Vimeo, YouTube, etc.) *
              </label>
              <input
                v-model="formData.video_url"
                type="url"
                placeholder="https://vimeo.com/123456789"
                class="form-input"
              />
              <small>
                Paste a Vimeo, YouTube, or other video platform URL
              </small>
            </div>
          </div>

          <!-- Video Details -->
          <div class="form-section">
            <h3>Video Details</h3>
            
            <div class="form-row">
              <div class="form-group">
                <label>Video Type</label>
                <select v-model="formData.video_type" class="form-select">
                  <option value="MAIN_CONTENT">Main Content</option>
                  <option value="INTRODUCTION">Introduction</option>
                  <option value="SUMMARY">Summary</option>
                  <option value="Q_AND_A">Q&A Session</option>
                  <option value="BONUS">Bonus Content</option>
                </select>
              </div>

              <div class="form-group">
                <label>Order</label>
                <input
                  v-model.number="formData.order"
                  type="number"
                  min="0"
                  class="form-input"
                />
                <small>Display order in course (0 = first)</small>
              </div>
            </div>

            <div class="form-group">
              <label>Duration (seconds)</label>
              <input
                v-model.number="formData.duration_seconds"
                type="number"
                min="0"
                placeholder="e.g., 3600 for 1 hour"
                class="form-input"
              />
              <small>Optional: Video length in seconds</small>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn btn-secondary">
            Cancel
          </button>
          <button
            @click="saveVideo"
            :disabled="!formData.title || !formData.course || !formData.video_url"
            class="btn btn-primary"
          >
            <i class="fas fa-save"></i>
            {{ editingVideo ? 'Update' : 'Add Video' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Timestamp Manager Modal -->
    <TimestampManager
      v-if="managingTimestampsFor"
      :video="managingTimestampsFor"
      @close="managingTimestampsFor = null"
      @updated="loadVideos"
    />

    <!-- Course Materials Manager Modal -->
    <CourseMaterialsManager
      v-if="managingMaterialsFor"
      :video="managingMaterialsFor"
      @close="managingMaterialsFor = null"
      @updated="loadVideos"
    />

    <!-- Transcript Analysis Modal -->
    <TranscriptAnalysisModal
      v-if="analyzingVideo"
      :video="analyzingVideo"
      @close="analyzingVideo = null"
      @updated="loadVideos"
    />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { lmsAPI } from '@/services/lmsAPI.js'
import { useToast } from 'vue-toastification'
import TimestampManager from './TimestampManager.vue'
import CourseMaterialsManager from './CourseMaterialsManager.vue'
import TranscriptAnalysisModal from './TranscriptAnalysisModal.vue'
import VideoCreationWizard from './VideoCreationWizard.vue'

export default {
  name: 'VideoManager',
  components: {
    TimestampManager,
    CourseMaterialsManager,
    TranscriptAnalysisModal,
    VideoCreationWizard
  },
  setup() {
    const toast = useToast()
    const videos = ref([])
    const courses = ref([])
    const loading = ref(true)
    const selectedCourseFilter = ref(null)
    const showCreateModal = ref(false)
    const editingVideo = ref(null)
    const managingTimestampsFor = ref(null)
    const analyzingVideo = ref(null)
    const formData = ref({
      course: null,
      title: '',
      description: '',
      video_url: '',
      video_type: 'MAIN_CONTENT',
      order: 0,
      duration_seconds: null
    })

    const loadVideos = async () => {
      loading.value = true
      try {
        const params = selectedCourseFilter.value ? { course: selectedCourseFilter.value } : {}
        const response = await lmsAPI.getVideos(params)
        videos.value = response.data.results || response.data
        
        // If materials manager modal is open, update its video reference with fresh data
        if (managingMaterialsFor.value) {
          const updatedVideo = videos.value.find(v => v.id === managingMaterialsFor.value.id)
          if (updatedVideo) {
            managingMaterialsFor.value = updatedVideo
            console.log('Updated materials manager modal with fresh video data')
          }
        }
      } catch (err) {
        console.error('Error loading videos:', err)
        toast.error('Failed to load videos')
      } finally {
        loading.value = false
      }
    }

    const loadCourses = async () => {
      try {
        const response = await lmsAPI.getCourses()
        courses.value = response.data.results || response.data
      } catch (err) {
        console.error('Error loading courses:', err)
      }
    }

    const editVideo = (video) => {
      editingVideo.value = video
      formData.value = {
        course: video.course,
        title: video.title,
        description: video.description || '',
        video_url: video.video_url || '',
        video_type: video.video_type,
        order: video.order,
        duration_seconds: video.duration_seconds
      }
    }

    const deleteVideo = async (video) => {
      if (!confirm(`Are you sure you want to delete "${video.title}"?`)) return

      try {
        await lmsAPI.deleteVideo(video.id)
        toast.success('Video deleted successfully')
        loadVideos()
      } catch (err) {
        console.error('Error deleting video:', err)
        toast.error('Failed to delete video')
      }
    }

    const saveVideo = async () => {
      try {
        const videoData = {
          course: formData.value.course,
          title: formData.value.title,
          description: formData.value.description,
          video_url: formData.value.video_url,
          video_type: formData.value.video_type,
          order: formData.value.order,
          duration_seconds: formData.value.duration_seconds,
          processing_status: 'COMPLETED'
        }

        if (editingVideo.value) {
          await lmsAPI.updateVideo(editingVideo.value.id, videoData)
          toast.success('Video updated successfully')
        } else {
          await lmsAPI.createVideo(videoData)
          toast.success('Video added successfully')
        }

        closeModal()
        loadVideos()
      } catch (err) {
        console.error('Error saving video:', err)
        console.error('Error response:', err.response)
        console.error('Error data:', err.response?.data)
        
        // Extract specific error message
        let errorMsg = 'Failed to save video'
        if (err.response?.data) {
          const errors = err.response.data
          if (typeof errors === 'object' && !errors.message && !errors.error) {
            // Field validation errors
            const fieldErrors = []
            for (const [field, msgs] of Object.entries(errors)) {
              const msgArray = Array.isArray(msgs) ? msgs : [msgs]
              fieldErrors.push(`• ${field}: ${msgArray.join(', ')}`)
            }
            if (fieldErrors.length > 0) {
              errorMsg = 'Validation errors:\n' + fieldErrors.join('\n')
            }
          } else {
            errorMsg = errors.message || errors.error || errors.detail || JSON.stringify(errors)
          }
        }
        
        toast.error(errorMsg, { timeout: 10000 })
        alert('Video Save Error:\n\n' + errorMsg)
      }
    }

    const handleVideoCreated = async (video) => {
      toast.success(`Video "${video.title}" created successfully!`)
      closeModal()
      await loadVideos()
    }

    const closeModal = () => {
      showCreateModal.value = false
      editingVideo.value = null
      formData.value = {
        course: null,
        title: '',
        description: '',
        video_url: '',
        video_type: 'MAIN_CONTENT',
        order: 0,
        duration_seconds: null
      }
    }

    const formatDuration = (seconds) => {
      if (!seconds) return 'Unknown'
      return lmsAPI.formatDuration(seconds)
    }

    const manageTimestamps = (video) => {
      managingTimestampsFor.value = video
    }

    const managingMaterialsFor = ref(null)
    
    const manageMaterials = (video) => {
      managingMaterialsFor.value = video
    }

    const generatingMomentsFor = ref(null)
    // Reactive array of video IDs currently being polled for completion
    const pollingVideoIds = ref([])
    // Non-reactive map: videoId → { intervalId, startTime }
    const pollingIntervals = {}
    const POLL_INTERVAL_MS = 6000
    const MAX_POLL_DURATION_MS = 6 * 60 * 1000 // give up after 6 min

    const stopPolling = (videoId) => {
      if (pollingIntervals[videoId]) {
        clearInterval(pollingIntervals[videoId].intervalId)
        delete pollingIntervals[videoId]
      }
      pollingVideoIds.value = pollingVideoIds.value.filter(id => id !== videoId)
    }

    const startPolling = (video) => {
      if (pollingVideoIds.value.includes(video.id)) return
      pollingVideoIds.value = [...pollingVideoIds.value, video.id]
      const startTime = Date.now()

      const intervalId = setInterval(async () => {
        if (Date.now() - startTime > MAX_POLL_DURATION_MS) {
          stopPolling(video.id)
          toast.warning(`Indexing "${video.title}" is taking longer than expected — check the worker logs.`, { timeout: 10000 })
          return
        }
        try {
          const response = await lmsAPI.getVideoMoments(video.id)
          const moments = response.data || []
          if (moments.length > 0) {
            stopPolling(video.id)
            // Patch the local video record so the badge updates immediately
            const idx = videos.value.findIndex(v => v.id === video.id)
            if (idx !== -1) {
              videos.value[idx] = { ...videos.value[idx], moments_count: moments.length }
            }
            toast.success(
              `"${video.title}" — ${moments.length} moment${moments.length === 1 ? '' : 's'} indexed and ready for search!`,
              { timeout: 8000 }
            )
          }
        } catch (err) {
          console.error('Moments poll error:', err)
          stopPolling(video.id)
        }
      }, POLL_INTERVAL_MS)

      pollingIntervals[video.id] = { intervalId, startTime }
    }

    const generateMoments = async (video) => {
      if (generatingMomentsFor.value === video.id) return
      generatingMomentsFor.value = video.id
      try {
        await lmsAPI.generateVideoMoments(video.id)
        toast.info(
          `Indexing started for "${video.title}" — this takes 1–3 min. The badge will update when done.`,
          { timeout: 7000 }
        )
        startPolling(video)
      } catch (err) {
        console.error('generateMoments error:', err)
        toast.error(
          err.response?.data?.error || err.response?.data?.detail || 'Failed to start moment generation',
          { timeout: 8000 }
        )
      } finally {
        generatingMomentsFor.value = null
      }
    }

    onUnmounted(() => {
      Object.values(pollingIntervals).forEach(({ intervalId }) => clearInterval(intervalId))
    })

    const analyzeVideo = (video) => {
      console.log('Analyze button clicked for video:', video.id)
      analyzingVideo.value = video
    }

    onMounted(async () => {
      await loadCourses()
      await loadVideos()
    })

    return {
      videos,
      courses,
      loading,
      selectedCourseFilter,
      showCreateModal,
      editingVideo,
      managingTimestampsFor,
      managingMaterialsFor,
      analyzingVideo,
      formData,
      loadVideos,
      editVideo,
      deleteVideo,
      saveVideo,
      handleVideoCreated,
      closeModal,
      manageTimestamps,
      manageMaterials,
      generatingMomentsFor,
      pollingVideoIds,
      generateMoments,
      analyzeVideo,
      formatDuration
    }
  }
}
</script>

<style scoped>
.video-manager {
  width: 100%;
}

.manager-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
}

.manager-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--color-text-primary);
}

.filter-section {
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-md);
  background: #f8f9fa;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.filter-section label {
  font-weight: 600;
  white-space: nowrap;
}

.filter-section .form-select {
  max-width: 400px;
}

.videos-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.video-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: var(--spacing-lg);
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  transition: all 0.2s ease;
}

.video-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.video-info {
  flex: 1;
}

.video-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-sm);
}

.video-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--color-text-primary);
}

.video-badges {
  display: flex;
  gap: var(--spacing-sm);
}

.type-badge,
.url-badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.type-badge {
  background: #e3f2fd;
  color: #1976d2;
}

.url-badge {
  background: #e8f5e9;
  color: #388e3c;
}

.video-description {
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 0.95rem;
}

.video-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-sm);
}

.video-meta span {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.video-url {
  margin-top: var(--spacing-sm);
  padding: var(--spacing-sm);
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.video-url a {
  color: var(--color-primary);
  text-decoration: none;
  word-break: break-all;
}

.video-url a:hover {
  text-decoration: underline;
}

.video-actions {
  display: flex;
  gap: var(--spacing-sm);
  flex-shrink: 0;
  margin-left: var(--spacing-md);
}

.video-source-info {
  background: #e3f2fd;
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-lg);
}

.video-source-info p {
  margin: 0 0 var(--spacing-sm) 0;
  color: #1565c0;
}

.video-source-info p:last-child {
  margin: 0;
}

.example-text {
  font-size: 0.9rem;
}

.example-text code {
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
}

/* Reuse common styles */
.loading-container,
.spinner,
.btn,
.btn-small,
.btn-primary,
.btn-secondary,
.btn-danger,
.empty-state,
.modal-overlay,
.modal-content,
.modal-header,
.modal-close,
.modal-body,
.modal-footer,
.form-group,
.form-input,
.form-textarea,
.form-select,
.form-row,
.form-section {
  /* Inherited from previous components */
}

.loading-container {
  text-align: center;
  padding: var(--spacing-xxl);
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--color-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-md);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius);
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-small {
  padding: var(--spacing-xs) var(--spacing-md);
  font-size: 0.9rem;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-secondary);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
}

.btn-secondary:hover {
  background: var(--color-primary);
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.empty-state {
  text-align: center;
  padding: var(--spacing-xxl);
  background: #f8f9fa;
  border-radius: var(--border-radius);
}

.empty-state i {
  font-size: 4rem;
  color: var(--color-text-secondary);
  opacity: 0.5;
  margin-bottom: var(--spacing-lg);
}

.empty-state h3 {
  margin: 0 0 var(--spacing-sm) 0;
}

.empty-state p {
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-lg) 0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

.modal-content {
  background: white;
  border-radius: var(--border-radius);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: var(--spacing-xl);
  border-bottom: 2px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: var(--spacing-sm);
}

.modal-close:hover {
  color: var(--color-text-primary);
}

.modal-body {
  padding: var(--spacing-xl);
}

.form-section {
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-xl);
  border-bottom: 1px solid var(--color-border);
}

.form-section:last-child {
  border-bottom: none;
}

.form-section h3 {
  margin: 0 0 var(--spacing-lg) 0;
  font-size: 1.1rem;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.form-group {
  margin-bottom: var(--spacing-lg);
}

.form-group label {
  display: block;
  margin-bottom: var(--spacing-xs);
  font-weight: 600;
  color: var(--color-text-primary);
}

.form-group small {
  display: block;
  margin-top: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: 0.85rem;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  font-size: 1rem;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
}

.modal-footer {
  padding: var(--spacing-lg) var(--spacing-xl);
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
}

.btn-moments {
  background: #7c3aed;
  color: white;
}

.btn-moments:hover:not(:disabled) {
  background: #6d28d9;
}

.btn-moments:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.moments-badge {
  color: #7c3aed;
  font-weight: 600;
}

.moments-badge--indexing {
  color: #d97706;
  animation: pulse-opacity 1.5s ease-in-out infinite;
}

@keyframes pulse-opacity {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}

@media (max-width: 768px) {
  .video-item {
    flex-direction: column;
  }

  .video-actions {
    width: 100%;
    margin-top: var(--spacing-md);
    margin-left: 0;
  }

  .video-actions .btn {
    flex: 1;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .filter-section {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-section .form-select {
    max-width: 100%;
  }
}
</style>

